import { useKeyboardShortcut } from '@bestseller-bit/frontend-community.hooks.use-keyboard-shortcut';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    FormControl,
    FormHelperText,
    InputLabel,
    OutlinedInput,
    Stack,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { DialogTitleWithCloseIcon } from '../DialogTitleWithCloseIcon/DialogTitleWithCloseIcon';

type CreateClusterDialogProps = {
    initialClusterName?: string;
    initialClusterDescription?: string;
    isEditing?: boolean;
    open: boolean;
    onOk: (name: string, description?: string) => void;
    onCancel: () => void;
};

export const CreateClusterDialog: FC<CreateClusterDialogProps> = ({
    initialClusterName = '',
    initialClusterDescription = '',
    isEditing = false,
    onOk,
    onCancel,
    open,
}) => {
    const [clusterName, setClusterName] = useState<string>(initialClusterName);
    const [clusterDescription, setClusterDescription] = useState<string>(initialClusterDescription);
    const [okClicked, setOkClicked] = useState<boolean>(false);

    const reset = () => {
        setClusterName(initialClusterName);
        setClusterDescription(initialClusterDescription);
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies: reset function is static
    useEffect(() => {
        // when dialog is opened, reset fields
        if (open) {
            reset();
        }
    }, [open]);

    const handleOkClicked = () => {
        if (isClusterNameInvalid) {
            setOkClicked(true);
            return;
        }

        onOk(clusterName, clusterDescription ?? '');
        setOkClicked(false);
    };

    const handleCancelClicked = () => {
        onCancel();
    };

    const isClusterNameInvalid = !clusterName || clusterName?.isEmpty() || clusterName.match(/^ *$/) !== null;

    const isClusterNameTooLong = !!clusterName && clusterName.length > 20;

    useKeyboardShortcut({
        shortcutName: 'SAVE',
        callback(event) {
            event.preventDefault();
            event.stopPropagation();
            handleOkClicked();
        },
        capture: true,
    });

    useKeyboardShortcut({
        shortcutName: 'CLEAR',
        callback(event) {
            event.preventDefault();
            event.stopPropagation();
            reset();
        },
        capture: true,
    });

    return (
        <Dialog
            maxWidth="xs"
            fullWidth
            open={open}
            onClose={onCancel}
            aria-labelledby="create-cluster-dialog-title"
            aria-describedby="create-cluster-dialog-description"
        >
            <DialogTitleWithCloseIcon
                id="create-cluster-dialog-title"
                title={isEditing ? 'Edit existing cluster' : 'Create new cluster'}
                handleClose={onCancel}
            />

            <DialogContent>
                <Stack spacing={2}>
                    <DialogContentText id="create-cluster-dialog-description">
                        {isEditing
                            ? 'You can edit the name and description of your cluster.'
                            : 'Please provide a cluster name and description for your new cluster.'}
                    </DialogContentText>

                    <FormControl>
                        <InputLabel htmlFor="component-outlined">Cluster Name</InputLabel>
                        <OutlinedInput
                            autoFocus
                            value={clusterName}
                            error={(isClusterNameInvalid || isClusterNameTooLong) && okClicked}
                            id="component-outlined"
                            label="Cluster Name"
                            onChange={(e) => setClusterName(e.currentTarget.value)}
                        />
                        {isClusterNameInvalid && okClicked && (
                            <FormHelperText id="component-error-text">
                                Please provide a valid cluster name.
                            </FormHelperText>
                        )}
                        {isClusterNameTooLong && okClicked && (
                            <FormHelperText id="component-error-text">
                                Cluster name cannot be over 20 characters.
                            </FormHelperText>
                        )}
                    </FormControl>
                    <FormControl>
                        <InputLabel htmlFor="component-outlined">Cluster Description</InputLabel>
                        <OutlinedInput
                            id="component-outlined"
                            label="Cluster Description"
                            value={clusterDescription}
                            onChange={(e) => setClusterDescription(e.currentTarget.value)}
                        />
                    </FormControl>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="text" onClick={handleCancelClicked}>
                    Cancel
                </Button>
                <Button color="info" onClick={handleOkClicked}>
                    {isEditing ? 'Save' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
